import { nanoid } from 'nanoid';
import { v1 as uuid } from 'uuid';
import type {
  AddResourceInput,
  CreateActionInput,
  CreateMissionInput,
  CreateReportInput,
  File,
  MapElementData,
  Mission,
  MissionReportSearchResults,
  MissionsApi,
  PatientUpdate,
  ResourceData,
  SectionData,
  UpdateMissionInput,
  UpdateResourceInput,
} from './api';

export class MissionsClient {
  constructor(private readonly api: MissionsApi) {}

  async createMission(values: CreateMissionInput): Promise<Mission> {
    return this.api.missions.createMission(uuid(), values);
  }

  async updateMission(id: string, values: UpdateMissionInput): Promise<Mission> {
    return this.api.missions.updateMission(id, values);
  }

  async createMissionAction(missionId: string, values: CreateActionInput): Promise<Mission> {
    return this.api.missions.createMissionAction(missionId, nanoid(), values);
  }

  async createMissionReport(missionId: string, values: CreateReportInput): Promise<Mission> {
    return this.api.missions.createMissionReport(missionId, nanoid(), values);
  }

  async createMissionResource(missionId: string, values: AddResourceInput): Promise<Mission> {
    return this.api.missions.createMissionResource(missionId, nanoid(), values);
  }

  async updateMissionResource(missionId: string, resourceId: string, values: UpdateResourceInput): Promise<Mission> {
    return this.api.missions.updateMissionResource(missionId, resourceId, values);
  }

  async setMissionResource(missionId: string, resourceId: string, values: ResourceData): Promise<Mission> {
    return this.api.missions.createMissionResource(missionId, resourceId, values);
  }

  async removeMissionResource(missionId: string, resourceId: string): Promise<Mission> {
    return this.api.missions.removeMissionResource(missionId, resourceId);
  }

  async endMission(missionId: string): Promise<void> {
    await this.api.missions.endMission(missionId);
  }

  async createMissionMapElement(missionId: string, values: MapElementData): Promise<Mission> {
    return this.api.missions.createMissionMapElement(missionId, nanoid(), values);
  }

  async updateMissionMapElement(missionId: string, id: string, values: MapElementData): Promise<Mission> {
    return this.api.missions.createMissionMapElement(missionId, id, values);
  }

  async deleteMissionMapElement(missionId: string, elementId: string): Promise<Mission> {
    return this.api.missions.removeMissionMapElement(missionId, elementId);
  }

  async createSection(missionId: string, id: string, values: SectionData): Promise<Mission> {
    return this.api.missions.createMissionSection(missionId, id, values);
  }

  async updateSection(missionId: string, id: string, values: SectionData): Promise<Mission> {
    return this.api.missions.updateMissionSection(missionId, id, values);
  }

  async deleteSection(missionId: string, id: string): Promise<Mission> {
    return this.api.missions.removeMissionSection(missionId, id);
  }

  async createPatient(missionId: string, id: string, values: PatientUpdate): Promise<Mission> {
    return this.api.missions.createMissionPatient(missionId, id, values);
  }

  async updatePatient(missionId: string, id: string, values: PatientUpdate): Promise<Mission> {
    return this.api.missions.updateMissionPatient(missionId, id, values);
  }

  async deletePatient(missionId: string, id: string): Promise<Mission> {
    return this.api.missions.removeMissionPatient(missionId, id);
  }

  async uploadFile(missionId: string, file: Blob): Promise<File> {
    const id = nanoid();
    const { url } = await this.api.missions.requestFileUpload(missionId, id, { type: file.type, size: file.size });
    await fetch(url, { method: 'PUT', body: file });
    return { id, type: file.type, size: file.size };
  }

  async getFileURL(missionId: string, fileId: string): Promise<string> {
    const { url } = await this.api.missions.getFile(missionId, fileId, true);
    return url;
  }

  async getReports(search: string, limit?: number): Promise<MissionReportSearchResults> {
    return this.api.reports.searchReports(search, limit);
  }

  async getReport(
    reportId: string,
    response: 'redirect' | 'url' | 'data' = 'data',
    contentType: 'application/json' | 'application/pdf' | 'application/zip'
  ): Promise<unknown> {
    return this.api.reports.getReport(reportId, response, contentType);
  }
}
